import React, { useState, useRef, useEffect } from 'react';
import styled from '@emotion/styled';
// Button component defined below as styled component
import {
  QrCodeScanner as ScannerIcon,
  Clear as ClearIcon,
  Visibility as ViewIcon,
  Delete as DeleteIcon,
  CameraAlt as CameraIcon,
  Close as CloseIcon,
  Inventory as InventoryIcon,
  Assignment as AssignmentIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { BrowserMultiFormatReader } from '@zxing/library';
import { doc, updateDoc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { LINE_STATUSES } from '../firebase/schema';
import ssApiService from '../services/ssApi';
import { testSanMarConnection } from '../services/supplierApi';
import OrderView from '../components/OrderView';
import JobSticker from '../components/JobSticker';
import ArrivalConfirmationModal from '../components/ArrivalConfirmationModal';
import { syncOrderStatusFromLines } from '../utils/orderStatusSync';
import { lineIsFinalized } from '../utils/lineCutStatus';
import { fetchStickerPrintPayload } from '../utils/fetchStickerPrintPayload';
import { printLabelBase64 } from '../utils/printServer';

const Container = styled.div`
  padding: ${props => props.theme.spacing.lg};
  margin-top: 48px;
  background-color: #000000;
  min-height: 100vh;
  
  @media (max-width: 768px) {
    padding: ${props => props.theme.spacing.md};
    margin-top: 0;
  }
`;

const StyledSection = styled.div`
  padding: ${props => props.theme.spacing.lg};
  margin: ${props => props.theme.spacing.md};
  background-color: #1e1e1e;
  color: white;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.3);
  
  @media (max-width: 768px) {
    padding: ${props => props.theme.spacing.md};
    margin: ${props => props.theme.spacing.sm};
    border-radius: 4px;
  }
`;

// ============ NEW DESKTOP LAYOUT COMPONENTS ============

const DesktopLayout = styled.div`
  display: none;
  
  @media (min-width: 769px) {
    display: block;
  }
`;

const MobileLayout = styled.div`
  display: block;
  
  @media (min-width: 769px) {
    display: none;
  }
`;

const DesktopHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 16px 24px;
  background: linear-gradient(135deg, #1e1e1e 0%, #2a2a2a 100%);
  border-radius: 12px;
  margin-top: 20px;
  margin-bottom: 20px;
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const HeaderTopRow = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  flex-wrap: wrap;
  justify-content: center;
`;

const ScannerInputRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  max-width: 520px;
  justify-content: center;
`;

const CompactScannerInput = styled.input`
  flex: 1;
  padding: 12px 20px;
  background-color: rgba(0, 0, 0, 0.4);
  border: 2px solid rgba(var(--al-primary-rgb), 0.3);
  border-radius: 8px;
  color: white;
  font-size: 1.1rem;
  font-family: 'Montserrat', monospace;
  font-weight: 600;
  letter-spacing: 0.05em;
  
  &:focus {
    outline: none;
    border-color: var(--al-primary);
    background-color: rgba(var(--al-primary-rgb), 0.1);
  }
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.3);
    font-weight: 400;
    font-size: 0.9rem;
  }
  
  &:disabled {
    opacity: 0.5;
  }
`;

const ScanIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: ${props => props.active ? 'rgba(var(--al-primary-rgb), 0.15)' : 'rgba(255, 255, 255, 0.05)'};
  border: 1px solid ${props => props.active ? 'rgba(var(--al-primary-rgb), 0.4)' : 'rgba(255, 255, 255, 0.1)'};
  border-radius: 6px;
  color: ${props => props.active ? 'var(--al-primary)' : 'rgba(255, 255, 255, 0.6)'};
  font-size: 0.85rem;
  font-weight: 500;
  white-space: nowrap;
`;

const TrackingSummary = styled.div`
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.7);
  margin-top: 4px;
  font-weight: 500;
`;

const StatusRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
`;

const SanMarStatusBlock = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const SanMarDot = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${props => {
    if (props.$status === 'connected') return '#4caf50';
    if (props.$status === 'disconnected') return '#f44336';
    if (props.$status === 'testing') return '#ff9800';
    return '#9e9e9e';
  }};
`;

const SupplierDot = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${props => (props.$connected ? '#4caf50' : '#6b6b6b')};
`;

const SupplierDotsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.85);
`;

const SupplierDotItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const InitiateReceivingButton = styled.button`
  padding: 12px 24px;
  font-size: 1rem;
  font-weight: 600;
  color: #1e1e1e;
  background: linear-gradient(135deg, var(--al-primary) 0%, #7ddc7d 100%);
  border: none;
  border-radius: 8px;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(var(--al-primary-rgb), 0.3);
  transition: transform 0.1s, box-shadow 0.2s;
  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(var(--al-primary-rgb), 0.4);
  }
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const TrackingCheckRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
  flex-wrap: wrap;
`;

const TrackingCheckInput = styled.input`
  padding: 8px 12px;
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(var(--al-primary-rgb), 0.3);
  border-radius: 6px;
  color: white;
  font-size: 0.9rem;
  min-width: 200px;
  max-width: 360px;
  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }
`;

const TrackingCheckResult = styled.div`
  font-size: 0.85rem;
  padding: 8px 12px;
  border-radius: 6px;
  margin-top: 4px;
  background: ${props => props.$found ? 'rgba(76, 175, 80, 0.15)' : 'rgba(244, 67, 54, 0.15)'};
  color: ${props => props.$found ? 'var(--al-primary)' : '#f44336'};
  border: 1px solid ${props => props.$found ? 'rgba(76, 175, 80, 0.4)' : 'rgba(244, 67, 54, 0.4)'};
`;

const MainGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 2fr 2fr;
  gap: 20px;
  height: calc(100vh - 210px);
  
  @media (max-width: 1400px) {
    grid-template-columns: 1fr 2fr 2fr;
  }
  
  @media (max-width: 1200px) {
    grid-template-columns: 1fr 2fr;
    
    & > *:last-child {
      display: none;
    }
  }
`;

const SidePanel = styled.div`
  background: #1e1e1e;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const CenterPanel = styled.div`
  background: #1e1e1e;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const PanelHeaderStyled = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: rgba(0, 0, 0, 0.3);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const PanelTitleStyled = styled.h3`
  color: white;
  margin: 0;
  font-size: 0.95rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const PanelBadge = styled.span`
  background: ${props => props.color || 'var(--al-primary)'};
  color: ${props => props.color ? 'white' : '#1e1e1e'};
  padding: 2px 10px;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 700;
`;

const PanelContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px;
`;

const ScannedBoxCard = styled.div`
  background: ${props => props.isLatest ? 'rgba(var(--al-primary-rgb), 0.1)' : 'rgba(0, 0, 0, 0.2)'};
  border: 1px solid ${props => props.isLatest ? 'rgba(var(--al-primary-rgb), 0.3)' : 'rgba(255, 255, 255, 0.1)'};
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 10px;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: rgba(var(--al-primary-rgb), 0.15);
    border-color: rgba(var(--al-primary-rgb), 0.4);
  }
  
  &:hover .box-actions {
    opacity: 1;
  }
`;

const BoxCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 8px;
`;

const BoxTrackingNumber = styled.div`
  font-family: 'Montserrat', monospace;
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--al-primary);
  word-break: break-all;
`;

const BoxInvoice = styled.div`
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.5);
`;

const BoxStats = styled.div`
  display: flex;
  gap: 12px;
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.7);
`;

const BoxActions = styled.div`
  display: flex;
  gap: 4px;
  opacity: 0;
  transition: opacity 0.2s;
`;

const SmallIconButton = styled.button`
  background: rgba(255, 255, 255, 0.1);
  border: none;
  color: rgba(255, 255, 255, 0.7);
  padding: 4px;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
    color: white;
  }
`;

const ContentGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const ProductRow = styled.div`
  display: grid;
  grid-template-columns: minmax(200px, 1fr) auto auto;
  align-items: center;
  gap: 16px;
  padding: 10px 16px;
  background: rgba(0, 0, 0, 0.2);
  border-left: 3px solid var(--al-primary);
  border-radius: 4px;
  
  &:hover {
    background: rgba(0, 0, 0, 0.3);
  }
`;

const ProductInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.85rem;
  color: white;
  font-weight: 500;
  overflow: hidden;
  
  .brand {
    color: var(--al-primary);
    font-weight: 600;
  }
  
  .separator {
    color: rgba(255, 255, 255, 0.3);
  }
  
  .color {
    color: rgba(255, 255, 255, 0.8);
  }
`;

const SizeGridRow = styled.div`
  display: flex;
  gap: 4px;
  flex-wrap: nowrap;
`;

const SizeCell = styled.div`
  background: rgba(var(--al-primary-rgb), 0.2);
  border: 1px solid rgba(var(--al-primary-rgb), 0.3);
  padding: 4px 8px;
  border-radius: 3px;
  font-size: 0.7rem;
  font-weight: 600;
  color: var(--al-primary);
  text-align: center;
  min-width: 36px;
  
  .size-label {
    font-size: 0.6rem;
    color: rgba(255, 255, 255, 0.5);
    display: block;
    margin-bottom: 1px;
  }
  
  .size-qty {
    font-weight: 700;
  }
`;

const TotalBadge = styled.div`
  background: var(--al-primary);
  color: #1e1e1e;
  padding: 6px 14px;
  border-radius: 4px;
  font-size: 0.8rem;
  font-weight: 700;
  white-space: nowrap;
`;

// Keep these for backwards compatibility with expected lines
const SizeGridCompact = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const SizeChipCompact = styled.div`
  background: var(--al-primary);
  color: #1e1e1e;
  padding: 3px 8px;
  border-radius: 4px;
  font-size: 0.7rem;
  font-weight: 700;
`;

const ExpectedCard = styled.div`
  background: ${props => props.matched ? 'rgba(76, 175, 80, 0.1)' : 'rgba(0, 0, 0, 0.2)'};
  border: 1px solid ${props => props.matched ? 'rgba(76, 175, 80, 0.3)' : 'rgba(255, 255, 255, 0.1)'};
  border-left: 3px solid ${props => props.matched ? '#4caf50' : 'rgba(255, 255, 255, 0.3)'};
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 10px;
`;

const ExpectedHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 8px;
`;

const ExpectedTitle = styled.div`
  font-weight: 600;
  color: white;
  font-size: 0.85rem;
`;

const ExpectedStatus = styled.div`
  font-size: 0.7rem;
  color: ${props => props.matched ? '#4caf50' : 'rgba(255, 255, 255, 0.5)'};
  display: flex;
  align-items: center;
  gap: 4px;
`;

const ExpectedMeta = styled.div`
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.6);
  margin-bottom: 8px;
`;

const SpeeDeeAlertBox = styled.div`
  background: rgba(255, 152, 0, 0.12);
  border: 1px solid rgba(255, 152, 0, 0.45);
  border-radius: 8px;
  padding: 12px;
  margin-top: 12px;
  font-size: 0.85rem;
  color: #ffb74d;
`;

const SpeeDeeAlertTitle = styled.div`
  font-weight: 600;
  margin-bottom: 6px;
`;

const SpeeDeeAlertItem = styled.div`
  margin-top: 6px;
  font-size: 0.8rem;
`;

const ExpectedSizeGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
`;

const ExpectedSizeCell = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 45px;
  padding: 6px 8px;
  border-radius: 4px;
  background: ${props => {
    if (props.fullyReceived) return 'rgba(76, 175, 80, 0.3)';
    if (props.partiallyReceived) return 'rgba(255, 193, 7, 0.2)';
    return 'rgba(255, 255, 255, 0.05)';
  }};
  border: 1px solid ${props => {
    if (props.fullyReceived) return 'rgba(76, 175, 80, 0.5)';
    if (props.partiallyReceived) return 'rgba(255, 193, 7, 0.4)';
    return 'rgba(255, 255, 255, 0.1)';
  }};
`;

const ExpectedSizeLabel = styled.div`
  font-size: 0.65rem;
  color: rgba(255, 255, 255, 0.5);
  margin-bottom: 2px;
`;

const ExpectedSizeQty = styled.div`
  font-size: 0.8rem;
  font-weight: 700;
  color: ${props => {
    if (props.fullyReceived) return '#4caf50';
    if (props.partiallyReceived) return '#ffc107';
    return 'rgba(255, 255, 255, 0.4)';
  }};
`;

const ReceivedIndicator = styled.div`
  font-size: 0.6rem;
  color: #4caf50;
  margin-top: 2px;
`;

const ExpectedTotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 10px;
  padding-top: 8px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  font-size: 0.75rem;
`;

const ReceivedCount = styled.span`
  color: #4caf50;
  font-weight: 600;
`;

const PendingCount = styled.span`
  color: rgba(255, 255, 255, 0.5);
`;

const MarkArrivedButton = styled.button`
  width: 100%;
  padding: 10px 16px;
  margin-top: 12px;
  background: linear-gradient(135deg, #4caf50 0%, #45a049 100%);
  border: none;
  border-radius: 6px;
  color: white;
  font-weight: 600;
  font-size: 0.8rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.2s;
  
  &:hover {
    background: linear-gradient(135deg, #45a049 0%, #3d8b40 100%);
    transform: translateY(-1px);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  svg {
    font-size: 1rem;
  }
`;

const EmptyStateStyled = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  color: rgba(255, 255, 255, 0.4);
  text-align: center;
  
  svg {
    font-size: 48px;
    margin-bottom: 16px;
    opacity: 0.5;
  }
`;

const SplitViewContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${props => props.theme.spacing.lg};
  margin-top: ${props => props.theme.spacing.lg};
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: ${props => props.theme.spacing.md};
  }
`;

const SplitPanel = styled.div`
  background-color: #1e1e1e;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  padding: ${props => props.theme.spacing.lg};
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.3);
  
  @media (max-width: 768px) {
    padding: ${props => props.theme.spacing.md};
    border-radius: 4px;
  }
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  margin-bottom: ${props => props.theme.spacing.md};
  padding-bottom: ${props => props.theme.spacing.sm};
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const PanelTitle = styled.h3`
  color: white;
  margin: 0;
  font-size: 1.125rem;
  font-weight: 600;
`;

const SectionTitle = styled.h2`
  color: white;
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

const FlexContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.md};
  margin-bottom: ${props => props.theme.spacing.md};
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: ${props => props.theme.spacing.sm};
  }
`;

const FlexBetween = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${props => props.theme.spacing.md};
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: ${props => props.theme.spacing.sm};
  }
`;

const Input = styled.input`
  flex: 1;
  padding: 12px 16px;
  background-color: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 4px;
  color: white;
  font-size: 14px;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  @media (max-width: 768px) {
    width: 100%;
    font-size: 16px; /* Prevents zoom on iOS */
  }
`;

const Button = styled.button`
  padding: 12px 24px;
  background-color: ${props => props.variant === 'outlined' ? 'transparent' : props.theme.colors.primary};
  color: ${props => props.variant === 'outlined' ? 'rgba(255, 255, 255, 0.7)' : 'white'};
  border: 1px solid ${props => props.variant === 'outlined' ? 'rgba(255, 255, 255, 0.3)' : props.theme.colors.primary};
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  min-width: ${props => props.minWidth || 'auto'};
  display: flex;
  align-items: center;
  gap: 8px;
  
  &:hover {
    background-color: ${props => props.variant === 'outlined' ? 'rgba(255, 255, 255, 0.1)' : props.theme.colors.primaryDark};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
    padding: 16px 24px;
    font-size: 16px;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.sm};
  
  @media (max-width: 768px) {
    flex-direction: column;
    width: 100%;
  }
`;

const Alert = styled.div`
  padding: 12px 16px;
  margin-bottom: ${props => props.theme.spacing.md};
  border-radius: 4px;
  background-color: ${props => props.severity === 'error' ? 'rgba(244, 67, 54, 0.1)' : 'rgba(76, 175, 80, 0.1)'};
  border: 1px solid ${props => props.severity === 'error' ? 'rgba(244, 67, 54, 0.3)' : 'rgba(76, 175, 80, 0.3)'};
  color: ${props => props.severity === 'error' ? '#f44336' : '#4caf50'};
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.sm};
`;

const ListItem = styled.div`
  background-color: rgba(255, 255, 255, 0.05);
  border-radius: 4px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  padding: ${props => props.theme.spacing.md};
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: ${props => props.theme.spacing.sm};
  }
`;

const ListItemContent = styled.div`
  flex: 1;
`;

const ListItemActions = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.sm};
  
  @media (max-width: 768px) {
    width: 100%;
    justify-content: flex-end;
  }
`;

const IconButton = styled.button`
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.7);
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }
  
  @media (max-width: 768px) {
    padding: 8px;
  }
`;

const Chip = styled.span`
  display: inline-block;
  padding: 4px 8px;
  background-color: ${props => props.color === 'primary' ? props.theme.colors.primary : 'rgba(255, 255, 255, 0.1)'};
  color: ${props => props.color === 'primary' ? 'white' : 'rgba(255, 255, 255, 0.8)'};
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  margin-right: 8px;
  margin-bottom: 4px;
`;

const Text = styled.div`
  color: ${props => props.color || 'rgba(255, 255, 255, 0.8)'};
  font-size: ${props => props.variant === 'subtitle2' ? '14px' : '16px'};
  font-weight: ${props => props.variant === 'subtitle2' ? '600' : '400'};
  margin-bottom: ${props => props.mb || '0'};
  
  @media (max-width: 768px) {
    font-size: ${props => props.variant === 'subtitle2' ? '13px' : '15px'};
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${props => props.theme.spacing.xl};
  color: rgba(255, 255, 255, 0.5);
  
  @media (max-width: 768px) {
    padding: ${props => props.theme.spacing.lg};
  }
`;

const InvoiceItem = styled.div`
  background-color: rgba(0, 0, 0, 0.2);
  border-radius: 4px;
  padding: ${props => props.theme.spacing.md};
  margin-bottom: ${props => props.theme.spacing.sm};
  border-left: 3px solid ${props => props.theme.colors.primary};
`;

const InvoiceItemHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: ${props => props.theme.spacing.sm};
`;

const InvoiceItemTitle = styled.div`
  font-weight: 600;
  color: white;
  font-size: 14px;
`;

const InvoiceItemQuantity = styled.div`
  background-color: ${props => props.theme.colors.primary};
  color: white;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
`;

const InvoiceItemDetails = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: ${props => props.theme.spacing.sm};
`;

const SizeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(40px, 1fr));
  gap: 4px;
  margin-top: ${props => props.theme.spacing.sm};
  max-width: 200px;
`;

const SizeChip = styled.div`
  background-color: ${props => props.theme.colors.primary};
  color: white;
  padding: 2px 6px;
  border-radius: 8px;
  font-size: 11px;
  font-weight: 600;
  text-align: center;
  min-width: 40px;
`;

const ExpectedLineItem = styled.div`
  background-color: rgba(0, 0, 0, 0.2);
  border-radius: 4px;
  padding: ${props => props.theme.spacing.md};
  margin-bottom: ${props => props.theme.spacing.sm};
  border-left: 3px solid ${props => props.isMatched ? '#4caf50' : 'rgba(255, 255, 255, 0.3)'};
  opacity: ${props => props.isMatched ? 1 : 0.7};
`;

const ExpectedLineHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: ${props => props.theme.spacing.sm};
`;

const ExpectedLineTitle = styled.div`
  font-weight: 600;
  color: white;
  font-size: 14px;
`;

const ExpectedLineStatus = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: ${props => props.isMatched ? '#4caf50' : 'rgba(255, 255, 255, 0.6)'};
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background-color: #1e1e1e;
  border-radius: 8px;
  padding: ${props => props.theme.spacing.lg};
  max-width: 90vw;
  max-height: 90vh;
  overflow-y: auto;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
  
  @media (max-width: 768px) {
    width: 95vw;
    height: 95vh;
    max-width: none;
    max-height: none;
    border-radius: 0;
    padding: ${props => props.theme.spacing.md};
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${props => props.theme.spacing.md};
`;

const ModalTitle = styled.h3`
  color: white;
  margin: 0;
  font-size: 1.125rem;
  font-weight: 600;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.7);
  cursor: pointer;
  font-size: 18px;
  padding: 4px;
  
  &:hover {
    color: white;
  }
`;

// Package Contents Modal Styled Components
const PackageModalContent = styled.div`
  background: linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%);
  border-radius: 16px;
  width: 700px;
  max-width: 95vw;
  max-height: 85vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  border: 1px solid rgba(var(--al-primary-rgb), 0.2);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.6);
`;

const PackageModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 24px;
  background: linear-gradient(135deg, rgba(var(--al-primary-rgb), 0.1) 0%, rgba(var(--al-primary-rgb), 0.05) 100%);
  border-bottom: 1px solid rgba(var(--al-primary-rgb), 0.2);
`;

const PackageHeaderInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const PackageTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 1.3rem;
  font-weight: 700;
  color: var(--al-primary);
  
  svg {
    font-size: 1.5rem;
  }
`;

const PackageSubtitle = styled.div`
  display: flex;
  gap: 16px;
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.6);
`;

const PackageModalCloseButton = styled.button`
  background: rgba(255, 255, 255, 0.1);
  border: none;
  color: rgba(255, 255, 255, 0.7);
  cursor: pointer;
  font-size: 20px;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
    color: white;
  }
`;

const PackageModalBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 20px 24px;
`;

const PackageSummaryBar = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 20px;
`;

const SummaryCard = styled.div`
  flex: 1;
  background: rgba(0, 0, 0, 0.3);
  border-radius: 10px;
  padding: 16px;
  text-align: center;
  border: 1px solid rgba(255, 255, 255, 0.08);
`;

const SummaryValue = styled.div`
  font-size: 1.8rem;
  font-weight: 700;
  color: ${props => props.color || 'var(--al-primary)'};
  margin-bottom: 4px;
`;

const SummaryLabel = styled.div`
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.5);
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const PackageItemsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const PackageItemRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto auto;
  align-items: center;
  gap: 16px;
  padding: 14px 16px;
  background: rgba(0, 0, 0, 0.25);
  border-radius: 8px;
  border-left: 3px solid var(--al-primary);
  
  &:hover {
    background: rgba(0, 0, 0, 0.35);
  }
`;

const PackageItemInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const PackageItemTitle = styled.div`
  font-weight: 600;
  color: white;
  font-size: 0.95rem;
`;

const PackageItemMeta = styled.div`
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.5);
`;

const PackageItemSizes = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  justify-content: flex-end;
`;

const PackageSizeChip = styled.div`
  background: rgba(var(--al-primary-rgb), 0.15);
  border: 1px solid rgba(var(--al-primary-rgb), 0.3);
  border-radius: 4px;
  padding: 4px 10px;
  font-size: 0.75rem;
  color: var(--al-primary);
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 40px;
  
  .size-label {
    font-weight: 400;
    font-size: 0.65rem;
    color: rgba(255, 255, 255, 0.5);
  }
  
  .size-qty {
    font-weight: 700;
  }
`;

const PackageItemTotal = styled.div`
  background: var(--al-primary);
  color: #1a1a1a;
  font-weight: 700;
  font-size: 0.9rem;
  padding: 8px 14px;
  border-radius: 6px;
  min-width: 50px;
  text-align: center;
`;

const PackageModalFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  background: rgba(0, 0, 0, 0.3);
  border-top: 1px solid rgba(255, 255, 255, 0.08);
`;

const PackageFooterInfo = styled.div`
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.6);
`;

const PackageFooterActions = styled.div`
  display: flex;
  gap: 10px;
`;

const PackageButton = styled.button`
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 8px;
  
  ${props => props.variant === 'primary' ? `
    background: var(--al-primary);
    border: none;
    color: #1a1a1a;
    
    &:hover {
      background: #7ddc7d;
    }
  ` : `
    background: transparent;
    border: 1px solid rgba(255, 255, 255, 0.2);
    color: rgba(255, 255, 255, 0.8);
    
    &:hover {
      background: rgba(255, 255, 255, 0.1);
      border-color: rgba(255, 255, 255, 0.3);
    }
  `}
`;

const LoadingSpinner = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const InitializingMessage = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${props => props.theme.spacing.md};
  padding: ${props => props.theme.spacing.xl};
  background-color: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.8);
  font-size: 16px;
  font-weight: 500;
  
  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.7; }
  }
  
  animation: pulse 2s infinite;
`;

const CameraModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: #000;
  z-index: 2000;
  display: flex;
  flex-direction: column;
`;

const CameraHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${props => props.theme.spacing.md};
  background-color: rgba(0, 0, 0, 0.8);
  color: white;
`;

const CameraContainer = styled.div`
  flex: 1;
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Video = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const CameraOverlay = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 250px;
  height: 150px;
  border: 2px solid ${props => props.theme.colors.primary};
  border-radius: 8px;
  pointer-events: none;
  
  &::before {
    content: '';
    position: absolute;
    top: -2px;
    left: -2px;
    right: -2px;
    bottom: -2px;
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-radius: 8px;
    animation: pulse 2s infinite;
  }
  
  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }
`;

const CameraInstructions = styled.div`
  position: absolute;
  bottom: ${props => props.theme.spacing.lg};
  left: 50%;
  transform: translateX(-50%);
  color: white;
  text-align: center;
  background-color: rgba(0, 0, 0, 0.7);
  padding: ${props => props.theme.spacing.sm};
  border-radius: 4px;
  font-size: 14px;
`;

// Desktop Scanner Section - Only shows on larger screens
const DesktopScannerSection = styled.div`
  display: none;
  
  @media (min-width: 769px) {
    display: block;
  }
`;

// Mobile Scanner Section - Only shows on smaller screens
const MobileScannerSection = styled.div`
  display: block;
  
  @media (min-width: 769px) {
    display: none;
  }
`;

const ScannerInputContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px 20px;
  background: rgba(0, 0, 0, 0.3);
  border: 2px dashed rgba(var(--al-primary-rgb), 0.3);
  border-radius: 8px;
  margin-top: 20px;
  transition: all 0.2s;
  
  &:focus-within {
    border-color: ${props => props.theme.colors.primary};
    background: rgba(var(--al-primary-rgb), 0.05);
  }
`;

const ScannerInputLabel = styled.div`
  font-size: 0.85em;
  color: rgba(255, 255, 255, 0.6);
  text-transform: uppercase;
  letter-spacing: 0.1em;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const LargeScannerInput = styled.input`
  width: 100%;
  max-width: 500px;
  padding: 20px 24px;
  background-color: rgba(255, 255, 255, 0.1);
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-radius: 4px;
  color: white;
  font-size: 1.5rem;
  font-family: 'Montserrat', monospace;
  font-weight: 600;
  text-align: center;
  letter-spacing: 0.1em;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    background-color: rgba(255, 255, 255, 0.15);
  }
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.3);
    font-weight: 400;
    font-size: 1rem;
    letter-spacing: 0.05em;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ScannerHint = styled.div`
  font-size: 0.75em;
  color: rgba(255, 255, 255, 0.4);
  margin-top: 16px;
  text-align: center;
`;

const LastScannedBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: rgba(var(--al-primary-rgb), 0.15);
  border: 1px solid rgba(var(--al-primary-rgb), 0.3);
  color: var(--al-primary);
  padding: 8px 16px;
  border-radius: 4px;
  font-size: 0.85em;
  margin-top: 16px;
`;

const APIReceiving = () => {
  const [trackingNumber, setTrackingNumber] = useState('');
  const [scannedBoxes, setScannedBoxes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderViewOpen, setOrderViewOpen] = useState(false);
  const [packageModalOpen, setPackageModalOpen] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  
  // Arrival confirmation modal state
  const [arrivalModalOpen, setArrivalModalOpen] = useState(false);
  const [selectedLineForArrival, setSelectedLineForArrival] = useState(null);
  const [isMarkingArrival, setIsMarkingArrival] = useState(false);
  const [stickerToPrint, setStickerToPrint] = useState(null);
  const [isPrinting, setIsPrinting] = useState(false);
  const [arrivalSuccessMessage, setArrivalSuccessMessage] = useState('');
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraError, setCameraError] = useState('');
  const [expectedLines, setExpectedLines] = useState([]);
  const [lastScanned, setLastScanned] = useState(null);
  
  // New state for tracking data management
  const [trackingMap, setTrackingMap] = useState({});
  const [trackingDataLoaded, setTrackingDataLoaded] = useState(false);
  const [initializing, setInitializing] = useState(false);
  const [trackingDataMetadata, setTrackingDataMetadata] = useState(null);

  // SanMar health check (temporary)
  const [sanmarStatus, setSanmarStatus] = useState('unknown');
  const [sanmarMessage, setSanmarMessage] = useState('');
  const [testingSanMar, setTestingSanMar] = useState(false);

  // SanMar: full order contents when all tracking numbers for an order have been scanned
  const [sanmarOrderContents, setSanmarOrderContents] = useState({});

  // Check if a tracking number is in the loaded list
  const [trackingCheckInput, setTrackingCheckInput] = useState('');
  const [trackingCheckResult, setTrackingCheckResult] = useState(null);
  
  const videoRef = useRef(null);
  const codeReader = useRef(null);
  const scannerInputRef = useRef(null);

  useEffect(() => {
    codeReader.current = new BrowserMultiFormatReader();
    
    return () => {
      if (codeReader.current) {
        codeReader.current.reset();
      }
    };
  }, []);

  // Load tracking data from localStorage on component mount and auto-initialize if needed
  useEffect(() => {
    const initializeTrackingData = async () => {
      const savedTrackingData = localStorage.getItem('ssTrackingData');
      if (savedTrackingData) {
        try {
          const data = JSON.parse(savedTrackingData);
          setTrackingMap(normalizeTrackingMap(data.trackingMap || {}));
          setTrackingDataMetadata(data.metadata);
          setTrackingDataLoaded(true);
          setSanmarStatus('connected');
          console.log('Loaded tracking data from localStorage:', data);
        } catch (error) {
          console.error('Error loading tracking data from localStorage:', error);
          // If there's an error loading saved data, auto-initialize
          await initializeAPI();
        }
      } else {
        // No saved data found, auto-initialize
        await initializeAPI();
      }
    };

    const initializeAPI = async () => {
      setInitializing(true);
      setError('');

      try {
        // Test API connection first
        console.log('Testing S&S API connection...');
        const connectionTest = await ssApiService.testConnection();
        
        if (!connectionTest.success) {
          throw new Error('S&S API connection failed');
        }

        console.log('S&S API connection successful, building tracking list...');
        console.log('📦 Fetching tracking data for last 10 days (S&S + SanMar when implemented)...');
        
        // Get tracking data for last 10 days (backend currently returns S&S only; SanMar not implemented yet)
        const trackingData = await ssApiService.getTrackingDataByDateRange(10);
        
        if (trackingData.success) {
          const { trackingMap, metadata } = trackingData.data;
          const keys = Object.keys(trackingMap);
          const bySupplier = { ssActivewear: 0, sanmar: 0 };
          keys.forEach(k => {
            const s = trackingMap[k]?.supplier || 'ssActivewear';
            if (s === 'sanmar') bySupplier.sanmar++;
            else bySupplier.ssActivewear++;
          });
          
          const normalizedMap = normalizeTrackingMap(trackingMap);
          const dataToSave = {
            trackingMap: normalizedMap,
            metadata,
            lastUpdated: new Date().toISOString()
          };
          
          localStorage.setItem('ssTrackingData', JSON.stringify(dataToSave));
          
          setTrackingMap(normalizedMap);
          setTrackingDataMetadata(metadata);
          setTrackingDataLoaded(true);
          setSanmarStatus('connected');
          
          console.log('📦 Tracking data received:', {
            totalEntries: keys.length,
            bySupplier: { 'S&S': bySupplier.ssActivewear, SanMar: bySupplier.sanmar },
            dateRange: `${metadata.startDate} – ${metadata.endDate}`
          });
          if (bySupplier.sanmar === 0) {
            console.log('📦 SanMar tracking: 0 (no SanMar shipments in date range, or SanMar API skipped/failed – check backend logs).');
          }
          if (metadata.speedeeDebug) {
            console.log('📦 SpeeDee debug:', metadata.speedeeDebug, '(ordersFromSS = orders returned by S&S; speedeeFlattened = SpeeDee found in those orders; firstOrderKeys = field names on first order)');
          }
          
          console.log('All tracking numbers in map:', keys);
          console.log('Full tracking map:', normalizedMap);
          
          setError('');
        } else {
          throw new Error(trackingData.error || 'Failed to load tracking data');
        }
      } catch (error) {
        console.error('Error initializing API:', error);
        setError(`Initialization failed: ${error.message}`);
      } finally {
        setInitializing(false);
      }
    };

    initializeTrackingData();
  }, []);

  // Initialize API and build tracking list (for manual refresh)
  const handleInitializeAPI = async () => {
    setInitializing(true);
    setError('');

    try {
      // Test API connection first
      console.log('Testing S&S API connection...');
      const connectionTest = await ssApiService.testConnection();
      
      if (!connectionTest.success) {
        throw new Error('S&S API connection failed');
      }

        console.log('S&S API connection successful, building tracking list...');
        console.log('📦 Fetching tracking data for last 10 days (S&S + SanMar when implemented)...');
        
        // Get tracking data for last 10 days (backend currently returns S&S only; SanMar not implemented yet)
        const trackingData = await ssApiService.getTrackingDataByDateRange(10);
        
        if (trackingData.success) {
          const { trackingMap, metadata } = trackingData.data;
          const keys = Object.keys(trackingMap);
          const bySupplier = { ssActivewear: 0, sanmar: 0 };
          keys.forEach(k => {
            const s = trackingMap[k]?.supplier || 'ssActivewear';
            if (s === 'sanmar') bySupplier.sanmar++;
            else bySupplier.ssActivewear++;
          });
          
          const normalizedMap = normalizeTrackingMap(trackingMap);
          const dataToSave = {
            trackingMap: normalizedMap,
            metadata,
            lastUpdated: new Date().toISOString()
          };
          
          localStorage.setItem('ssTrackingData', JSON.stringify(dataToSave));
          
          setTrackingMap(normalizedMap);
          setTrackingDataMetadata(metadata);
          setTrackingDataLoaded(true);
          setSanmarStatus('connected');
          
          console.log('📦 Tracking data received:', {
            totalEntries: keys.length,
            bySupplier: { 'S&S': bySupplier.ssActivewear, SanMar: bySupplier.sanmar },
            dateRange: `${metadata.startDate} – ${metadata.endDate}`
          });
          if (bySupplier.sanmar === 0) {
            console.log('📦 SanMar tracking: 0 (no SanMar shipments in date range, or SanMar API skipped/failed – check backend logs).');
          }
          if (metadata.speedeeDebug) {
            console.log('📦 SpeeDee debug:', metadata.speedeeDebug, '(ordersFromSS = orders returned by S&S; speedeeFlattened = SpeeDee found in those orders; firstOrderKeys = field names on first order)');
          }
          
          console.log('All tracking numbers in map:', keys);
          console.log('Full tracking map:', normalizedMap);
          
          setError('');
        } else {
          throw new Error(trackingData.error || 'Failed to load tracking data');
        }
      } catch (error) {
        console.error('Error initializing API:', error);
      setError(`Initialization failed: ${error.message}`);
    } finally {
      setInitializing(false);
    }
  };

  // SanMar health check (temporary)
  const handleTestSanMar = async () => {
    setTestingSanMar(true);
    setSanmarStatus('testing');
    setSanmarMessage('Testing...');
    try {
      const result = await testSanMarConnection();
      if (result.success) {
        setSanmarStatus('connected');
        setSanmarMessage(result.environment === 'production' ? 'Connected (Production)' : 'Connected (Test)');
      } else {
        setSanmarStatus('disconnected');
        setSanmarMessage(result.error || result.message || 'Connection failed');
      }
    } catch (err) {
      setSanmarStatus('disconnected');
      setSanmarMessage(err.message || 'Test failed');
    } finally {
      setTestingSanMar(false);
    }
  };

  // Extract tracking number from pasted text or UPS URL (e.g. tracknum=1Z8118920352036147)
  const extractTrackingNumber = (text) => {
    if (!text || typeof text !== 'string') return '';
    const trimmed = text.trim();
    const tracknumMatch = trimmed.match(/tracknum=([A-Z0-9]+)/i);
    if (tracknumMatch) return tracknumMatch[1].toUpperCase();
    const upsMatch = trimmed.match(/(1Z[A-Z0-9]{16})/i);
    if (upsMatch) return upsMatch[1].toUpperCase();
    return trimmed.replace(/\s+/g, '').toUpperCase();
  };

  // SpeeDee multi-box: last 3 digits are package number (001, 002, ... 014). Normalize to base+001 for lookup so all boxes resolve to the same invoice.
  const canonicalSpeeDeeForLookup = (tn) => {
    if (!tn || typeof tn !== 'string') return tn;
    const s = tn.trim();
    if (s.startsWith('SP') && /^SP\d{20,}$/.test(s)) return s.slice(0, -3) + '001';
    return s;
  };

  // SpeeDee: last 3 digits = box number (001, 002, ... 014). Parse for tally.
  const getSpeeDeeBoxNumber = (tn) => {
    if (!tn || typeof tn !== 'string') return null;
    const s = tn.trim();
    if (!s.startsWith('SP') || !/^SP\d{20,}$/.test(s)) return null;
    const last3 = s.slice(-3);
    const num = parseInt(last3, 10);
    return Number.isNaN(num) ? null : num;
  };

  // SanMar: backend or cached data may have orderNumber "Not Available"; use salesOrderNumber so UI and logic work
  const normalizeTrackingMap = (map) => {
    if (!map || typeof map !== 'object') return map;
    const out = { ...map };
    Object.keys(out).forEach((key) => {
      const entry = out[key];
      if (entry?.supplier === 'sanmar') {
        const o = entry.orderNumber;
        const so = entry.salesOrderNumber;
        if (!o || String(o).trim() === '' || String(o) === 'Not Available') {
          out[key] = { ...entry, orderNumber: so || o || null };
        }
      }
    });
    return out;
  };

  // Per-shipment SpeeDee box tally: group scanned boxes by canonical key, expected total = max box # scanned, missing = gaps in 1..expectedTotal.
  const speeDeeShipmentStats = React.useMemo(() => {
    const byCanonical = {};
    scannedBoxes.forEach((box) => {
      const tn = box.trackingNumber?.trim();
      const boxNum = getSpeeDeeBoxNumber(tn);
      if (boxNum == null) return;
      const canonical = canonicalSpeeDeeForLookup(tn);
      if (!byCanonical[canonical]) {
        byCanonical[canonical] = {
          canonical,
          invoiceNumber: box.orderData?.tracking?.invoiceNumber || box.orderData?.invoice?.invoiceNumber || '—',
          orderNumber: box.orderData?.tracking?.orderNumber || '—',
          scannedBoxNumbers: new Set()
        };
      }
      byCanonical[canonical].scannedBoxNumbers.add(boxNum);
    });
    return Object.values(byCanonical).map((s) => {
      const nums = Array.from(s.scannedBoxNumbers);
      const expectedTotal = nums.length === 0 ? 0 : Math.max(...nums);
      const missing = [];
      for (let i = 1; i <= expectedTotal; i++) {
        if (!s.scannedBoxNumbers.has(i)) missing.push(i);
      }
      return {
        canonical: s.canonical,
        invoiceNumber: s.invoiceNumber,
        orderNumber: s.orderNumber,
        expectedTotal,
        missing,
        scannedCount: s.scannedBoxNumbers.size
      };
    });
  }, [scannedBoxes]);

  // SanMar: one order = multiple tracking numbers; track which are scanned; when all scanned, we can show full order contents
  const sanmarOrderStats = React.useMemo(() => {
    const byOrder = {};
    scannedBoxes.forEach((box) => {
      if (box.orderData?.tracking?.supplier !== 'sanmar') return;
      const orderNum = (box.orderData?.tracking?.orderNumber && String(box.orderData.tracking.orderNumber) !== 'Not Available')
        ? box.orderData.tracking.orderNumber
        : box.orderData?.tracking?.salesOrderNumber;
      if (!orderNum) return;
      if (!byOrder[orderNum]) {
        byOrder[orderNum] = {
          orderNumber: orderNum,
          orderTrackingNumbers: box.orderData?.tracking?.orderTrackingNumbers || [],
          scannedTrackingNumbers: new Set()
        };
      }
      byOrder[orderNum].scannedTrackingNumbers.add(box.trackingNumber?.trim());
    });
    return Object.values(byOrder).map((o) => {
      const expected = o.orderTrackingNumbers && o.orderTrackingNumbers.length > 0 ? o.orderTrackingNumbers : Array.from(o.scannedTrackingNumbers);
      const scanned = Array.from(o.scannedTrackingNumbers);
      const missing = expected.filter((tn) => !o.scannedTrackingNumbers.has(tn));
      return {
        orderNumber: o.orderNumber,
        expectedTrackingNumbers: expected,
        scannedTrackingNumbers: scanned,
        scannedCount: scanned.length,
        totalCount: expected.length,
        missingTrackingNumbers: missing,
        allScanned: missing.length === 0 && expected.length > 0
      };
    });
  }, [scannedBoxes]);

  // When all tracking numbers for a SanMar order are scanned, fetch full order contents and attach to boxes
  const fetchedSanMarOrdersRef = React.useRef(new Set());
  React.useEffect(() => {
    const complete = sanmarOrderStats.filter((s) => s.allScanned && s.orderNumber);
    complete.forEach((stat) => {
      const orderNum = stat.orderNumber;
      if (fetchedSanMarOrdersRef.current.has(orderNum)) return;
      fetchedSanMarOrdersRef.current.add(orderNum);
      ssApiService.getSanMarOrderDetails(orderNum).then(async (result) => {
        if (result.success && result.data?.invoice) {
          const invoice = result.data.invoice;
          setSanmarOrderContents((prev) => ({ ...prev, [orderNum]: { invoice } }));
          setScannedBoxes((prev) =>
            prev.map((box) => {
              if (box.orderData?.tracking?.supplier !== 'sanmar') return box;
              const o = box.orderData?.tracking?.orderNumber;
              const so = box.orderData?.tracking?.salesOrderNumber;
              const match = (o && String(o) !== 'Not Available' && o === orderNum) || so === orderNum;
              if (!match) return box;
              return { ...box, orderData: { ...box.orderData, invoice } };
            })
          );
          if (invoice?.lines?.length) {
            const allLines = await getExpectedLines();
            const invoiceItems = (invoice.lines || []).map(normalizeInvoiceLine).filter(Boolean);
            const matchingLines = getMatchingLines(invoiceItems, allLines);
            setExpectedLines((prev) => {
              const byId = new Map(prev.map((l) => [l.id, l]));
              matchingLines.forEach((l) => byId.set(l.id, l));
              return Array.from(byId.values());
            });
          }
        }
      }).catch(() => {
        fetchedSanMarOrdersRef.current.delete(orderNum);
      });
    });
  }, [sanmarOrderStats]);

  const handleCheckTracking = () => {
    setTrackingCheckResult(null);
    const number = extractTrackingNumber(trackingCheckInput);
    if (!number) {
      setTrackingCheckResult({ found: false, number: '', message: 'Enter a tracking number or paste a UPS tracking URL.' });
      return;
    }
    const keys = Object.keys(trackingMap);
    const exact = trackingMap[number];
    const normalized = keys.find(k => k.replace(/\s|\./g, '') === number.replace(/\s|\./g, ''));
    const canonical = canonicalSpeeDeeForLookup(number);
    const info = exact || (normalized ? trackingMap[normalized] : null) || (canonical !== number ? trackingMap[canonical] : null);
    if (info) {
      setTrackingCheckResult({
        found: true,
        number: exact ? number : (normalized || (canonical !== number ? canonical : number)),
        info
      });
    } else {
      setTrackingCheckResult({ found: false, number, message: 'Not in loaded list. Load tracking data and ensure the shipment is within the date range.' });
    }
  };

  // Fetch real order lines that are "ordered" but not yet "IN" (waiting on garments)
  const getExpectedLines = async () => {
    try {
      const linesQuery = query(
        collection(db, 'orderLines'),
        where('status', 'in', [LINE_STATUSES.ORDERED, LINE_STATUSES.CUT_NOT_IN])
      );
      const linesSnapshot = await getDocs(linesQuery);
      const linesList = linesSnapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() }));

      if (linesList.length === 0) {
        return [];
      }

      const orderIds = [...new Set(linesList.map(line => line.orderId))];
      const ordersData = {};
      for (const orderId of orderIds) {
        const orderSnap = await getDoc(doc(db, 'orders', orderId));
        if (orderSnap.exists()) {
          ordersData[orderId] = { id: orderSnap.id, ...orderSnap.data() };
        }
      }

      return linesList.map(line => {
        const order = ordersData[line.orderId] || {};
        const sizes = line.sizes && typeof line.sizes === 'object' ? line.sizes : {};
        const totalPieces = line.totalQuantity ?? Object.values(sizes).reduce((sum, q) => sum + (Number(q) || 0), 0);
        return {
          id: line.id,
          orderId: line.orderId,
          orderNumber: order.poNumber || order.orderNumber || order.id?.slice(-6) || '—',
          customerName: order.customerName || 'Unknown',
          designName: line.designName || 'Unnamed',
          garmentStyle: line.garmentId || line.garmentStyle || 'N/A',
          garmentColor: line.garmentColor || 'N/A',
          garmentId: line.garmentId,
          sizes,
          totalPieces,
          totalQuantity: totalPieces,
          status: line.status
        };
      });
    } catch (err) {
      console.error('Error fetching expected lines:', err);
      return [];
    }
  };

  // S&S API may return PascalCase; normalize so we always have camelCase for matching and display.
  const normalizeInvoiceLine = (line) => {
    if (!line || typeof line !== 'object') return null;
    return {
      title: line.title ?? line.Title ?? '',
      brandName: line.brandName ?? line.BrandName ?? '',
      styleName: line.styleName ?? line.StyleName ?? '',
      colorName: line.colorName ?? line.ColorName ?? '',
      sizeName: line.sizeName ?? line.SizeName ?? '',
      qtyOrdered: line.qtyOrdered ?? line.QtyOrdered ?? 0
    };
  };

  // Match priority: (1) Style number e.g. LS14001, (2) color, (3) quantities.
  // Build one searchable string from package item so "Lane Seven - LS14001 - Army Green" is fully searchable.
  const styleMatches = (expectedStyle, itemTitle, itemBrand, itemStyle, expectedColor = '') => {
    const e = (expectedStyle || '').toLowerCase().trim();
    const colorNorm = (expectedColor || '').toLowerCase().trim();
    const title = (itemTitle || '').toLowerCase();
    const brand = (itemBrand || '').toLowerCase();
    const style = (itemStyle || '').toLowerCase();
    const combined = [title, brand, style].filter(Boolean).join(' ');
    if (!e) return false;
    if (combined.includes(e) || title.includes(e) || brand.includes(e) || style.includes(e)) return true;
    if (e.includes(title) || e.includes(brand) || e.includes(style)) return true;
    const rawParts = e.split(/\s*-\s*/).filter(Boolean);
    const colorWords = /^(black|white|navy|red|grey|gray|blue|green|heather|oatmeal|toast|bone|walnut|charcoal|ash|pale|royal|true|army)$/i;
    const nonColorParts = rawParts.filter(
      part => part.length > 1 && part !== colorNorm && !colorWords.test(part.trim())
    );
    const hasStyleNumber = (part) => /\d/.test(part);
    const styleNumberParts = nonColorParts.filter(hasStyleNumber);
    const partsToMatch = styleNumberParts.length > 0 ? styleNumberParts : nonColorParts;
    if (partsToMatch.length === 0) return false;
    return partsToMatch.some(part => combined.includes(part) || title.includes(part) || style.includes(part) || brand.includes(part));
  };

  // Match package contents to expected lines: (1) style number e.g. LS14001, (2) color, (3) quantities for received/expected.
  const getMatchingLines = (invoiceItems, allExpectedLines) => {
    if (!invoiceItems || invoiceItems.length === 0) {
      return [];
    }

    return allExpectedLines.filter(expectedLine => {
      return invoiceItems.some(invoiceItem => {
        const itemColor = (invoiceItem.colorName || '').toLowerCase().trim();
        const expectedColor = (expectedLine.garmentColor || '').toLowerCase().trim();
        const styleMatch = styleMatches(
          expectedLine.garmentStyle || expectedLine.garmentId,
          invoiceItem.title,
          invoiceItem.brandName,
          invoiceItem.styleName,
          expectedLine.garmentColor
        );
        const colorMatch = !expectedColor || !itemColor || itemColor.includes(expectedColor) || expectedColor.includes(itemColor);
        return styleMatch && colorMatch;
      });
    });
  };

  // Check if an expected line matches any invoice items
  const isLineMatched = (expectedLine, invoiceItems) => {
    if (!invoiceItems || !expectedLine) return false;
    const expectedColor = (expectedLine.garmentColor || '').toLowerCase().trim();
    return invoiceItems.some(item => {
      const itemColor = (item.colorName || '').toLowerCase().trim();
      const styleMatch = styleMatches(
        expectedLine.garmentStyle || expectedLine.garmentId,
        item.title,
        item.brandName,
        item.styleName,
        expectedLine.garmentColor
      );
      const colorMatch = !expectedColor || !itemColor || itemColor.includes(expectedColor) || expectedColor.includes(itemColor);
      return styleMatch && colorMatch;
    });
  };

  // Calculate received quantities for an expected line based on scanned items
  const getReceivedQuantities = (expectedLine, groupedInvoiceItems) => {
    if (!groupedInvoiceItems || groupedInvoiceItems.length === 0 || !expectedLine) {
      return {};
    }

    const receivedSizes = {};
    const expectedStyle = expectedLine.garmentStyle?.toLowerCase() || expectedLine.garmentId?.toLowerCase() || '';
    const expectedColor = (expectedLine.garmentColor || '').toLowerCase().trim();

    groupedInvoiceItems.forEach(item => {
      const styleMatch = styleMatches(
        expectedStyle,
        item.title,
        item.brandName,
        item.styleName,
        expectedLine.garmentColor
      );
      const itemColor = (item.colorName || '').toLowerCase().trim();
      const colorMatch = !expectedColor || !itemColor || itemColor.includes(expectedColor) || expectedColor.includes(itemColor);

      if (styleMatch && colorMatch) {
        Object.entries(item.sizes || {}).forEach(([size, qty]) => {
          receivedSizes[size] = (receivedSizes[size] || 0) + qty;
        });
      }
    });

    return receivedSizes;
  };

  const startCamera = async () => {
    try {
      console.log('🟢 Starting camera...');
      setCameraError('');
      setCameraOpen(true);
      
      // Check if codeReader is available
      if (!codeReader.current) {
        throw new Error('Barcode reader not initialized');
      }
      
      // For iOS Safari, we need to handle camera detection differently
      let selectedDeviceId = null;
      
      try {
        console.log('🟢 Listing video input devices...');
        const videoInputDevices = await codeReader.current.listVideoInputDevices();
        console.log('🟢 Available devices:', videoInputDevices);
        
        // Find the rear camera
        const rearCamera = videoInputDevices.find(device => 
          device.label.toLowerCase().includes('back') || 
          device.label.toLowerCase().includes('rear') ||
          device.label.toLowerCase().includes('environment')
        );
        
        if (rearCamera) {
          console.log('🟢 Found rear camera:', rearCamera);
          selectedDeviceId = rearCamera.deviceId;
        } else if (videoInputDevices.length > 0) {
          console.log('🟡 No rear camera found, using first available camera');
          selectedDeviceId = videoInputDevices[0].deviceId;
        }
      } catch (deviceError) {
        console.log('🟡 Device listing failed, trying fallback method...');
      }
      
      // If no device found, try using 'environment' (which should use rear camera)
      if (!selectedDeviceId) {
        console.log('🟡 No specific device found, using environment facing camera...');
        try {
          // First try to get the environment-facing camera
          const constraints = {
            video: {
              facingMode: 'environment'
            }
          };
          const stream = await navigator.mediaDevices.getUserMedia(constraints);
          const devices = await navigator.mediaDevices.enumerateDevices();
          const videoDevices = devices.filter(device => device.kind === 'videoinput');
          
          // Get the device ID from the active stream
          const activeTrack = stream.getVideoTracks()[0];
          if (activeTrack) {
            const activeDeviceId = videoDevices.find(device => 
              device.label.includes(activeTrack.label)
            )?.deviceId;
            
            if (activeDeviceId) {
              console.log('🟢 Found environment camera device ID:', activeDeviceId);
              selectedDeviceId = activeDeviceId;
            }
          }
          
          // Clean up the test stream
          stream.getTracks().forEach(track => track.stop());
        } catch (err) {
          console.log('🟡 Could not get environment camera, falling back to default...');
          selectedDeviceId = undefined;
        }
      }

      console.log('🟢 Starting decode from video device with ID:', selectedDeviceId);
      await codeReader.current.decodeFromVideoDevice(
        selectedDeviceId,
        videoRef.current,
        (result, err) => {
          if (result) {
            console.log('🟢 Barcode detected:', result.getText());
            const scannedCode = result.getText();
            
            // Filter out QR codes that contain URLs
            if (scannedCode.startsWith('http://') || scannedCode.startsWith('https://')) {
              console.log('🟡 Ignoring URL QR code:', scannedCode);
              return;
            }
            
            setTrackingNumber(scannedCode);
            setCameraOpen(false);
            handleScanSubmit(scannedCode);
          }
          if (err && !(err instanceof Error)) {
            // Ignore NotFoundException errors (no barcode found)
            console.log('No barcode found in frame');
          }
        }
      );
      
      console.log('🟢 Camera started successfully');
    } catch (error) {
      console.error('🔴 Camera error:', error);
      setCameraError(`Camera error: ${error.message}`);
      setCameraOpen(false);
      
      // Show error in UI for debugging on mobile
      alert(`Camera Error: ${error.message}`);
    }
  };

  const stopCamera = () => {
    if (codeReader.current) {
      codeReader.current.reset();
    }
    setCameraOpen(false);
    setCameraError('');
  };

  // Handle tracking number submission
  const handleScanSubmit = async (code = null) => {
    const numberToScan = code || trackingNumber;
    
    if (!numberToScan.trim()) {
      setError('Please enter a tracking number');
      return;
    }

    // Check for duplicate - prevent scanning the same tracking number twice
    const isDuplicate = scannedBoxes.some(
      box => box.trackingNumber.toLowerCase() === numberToScan.trim().toLowerCase()
    );
    
    if (isDuplicate) {
      setError(`Tracking number ${numberToScan} has already been scanned`);
      setTrackingNumber('');
      // Refocus for next scan
      if (scannerInputRef.current) {
        scannerInputRef.current.focus();
      }
      return;
    }

    if (!trackingDataLoaded) {
      setError('Please initialize the API first to load tracking data');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Debug: Log the tracking map and the number we're looking for
      console.log('Current tracking map keys:', Object.keys(trackingMap));
      console.log('Looking for tracking number:', numberToScan);
      console.log('Tracking map contains this number?', numberToScan in trackingMap);
      
      // Look up tracking number in local mapping (SpeeDee 002–014 resolve to same invoice as 001)
      const canonicalSpeeDee = numberToScan.startsWith('SP') && /^SP\d{20,}$/.test(numberToScan)
        ? numberToScan.slice(0, -3) + '001'
        : numberToScan;
      const trackingInfo = trackingMap[numberToScan] || trackingMap[canonicalSpeeDee];

      if (!trackingInfo) {
        console.log('Tracking number not found in map. Available numbers:', Object.keys(trackingMap));
        setError(`Tracking number "${numberToScan}" not found in recent shipments.`);
        setTrackingNumber('');
        setLoading(false);
        // Refocus for next scan
        if (scannerInputRef.current) {
          scannerInputRef.current.focus();
        }
        return;
      }

      console.log('Found tracking info:', trackingInfo);

      const isSanMar = trackingInfo.supplier === 'sanmar';
      const hasSSInvoice = trackingInfo.invoiceNumber && String(trackingInfo.invoiceNumber).trim();
      const hasSSOrder = trackingInfo.orderNumber && String(trackingInfo.orderNumber).trim() && String(trackingInfo.orderNumber) !== 'Not Available';

      if (isSanMar || !hasSSInvoice || !hasSSOrder) {
        // SanMar or missing S&S invoice/order: don't call S&S getInvoiceDetails.
        // SanMar assigns one sales order to multiple tracking numbers (all boxes in the shipment);
        // we have no per-tracking breakdown of contents, so "order contents" = full order when API supports it.
        const sanmarOrderNumber = (trackingInfo.orderNumber && String(trackingInfo.orderNumber).trim() && String(trackingInfo.orderNumber) !== 'Not Available')
          ? trackingInfo.orderNumber
          : (trackingInfo.salesOrderNumber || trackingInfo.orderNumber || null);
        let invoice = null;
        if (isSanMar && sanmarOrderNumber && String(sanmarOrderNumber).trim() && String(sanmarOrderNumber) !== 'Not Available') {
          try {
            const sanmarResult = await ssApiService.getSanMarOrderDetails(sanmarOrderNumber);
            if (sanmarResult.success && sanmarResult.data?.invoice) {
              invoice = sanmarResult.data.invoice;
            }
          } catch (e) {
            console.warn('SanMar order details not available:', e?.message);
          }
        }
        const newBox = {
          id: Date.now(),
          trackingNumber: numberToScan,
          orderData: {
            invoice,
            tracking: {
              trackingNumber: numberToScan,
              invoiceNumber: trackingInfo.invoiceNumber || null,
              orderNumber: sanmarOrderNumber || trackingInfo.orderNumber || null,
              salesOrderNumber: trackingInfo.salesOrderNumber || null,
              orderTrackingNumbers: trackingInfo.orderTrackingNumbers || null,
              carrierName: trackingInfo.carrierName || 'UPS',
              supplier: trackingInfo.supplier || null
            }
          },
          scannedAt: new Date(),
          status: 'scanned'
        };
        setScannedBoxes(prev => [...prev, newBox]);
        setLastScanned(numberToScan);
        setTrackingNumber('');
        setError('');
        if (invoice?.lines?.length) {
          const allLines = await getExpectedLines();
          const invoiceItems = (invoice.lines || []).map(normalizeInvoiceLine).filter(Boolean);
          const matchingLines = getMatchingLines(invoiceItems, allLines);
          setExpectedLines(matchingLines);
        } else {
          setExpectedLines([]);
          if (isSanMar) {
            setError('SanMar: order added (Order #' + (sanmarOrderNumber || '—') + '). Package contents not available from API yet.');
            setTimeout(() => setError(''), 6000);
          }
        }
        if (scannerInputRef.current) scannerInputRef.current.focus();
        return;
      }

      // S&S tracking with valid invoice/order: fetch invoice details
      console.log('🔍 About to call getInvoiceDetails with invoice number:', trackingInfo.invoiceNumber, 'and order number:', trackingInfo.orderNumber);
      const invoiceResult = await ssApiService.getInvoiceDetails(trackingInfo.invoiceNumber, trackingInfo.orderNumber);

      if (invoiceResult.success) {
        console.log('✅ Invoice details received:', invoiceResult.data);
        console.log('📋 Invoice number in response:', invoiceResult.data.invoice?.invoiceNumber);
        console.log('🎯 Expected invoice number:', trackingInfo.invoiceNumber);
        console.log('❌ Match?', invoiceResult.data.invoice?.invoiceNumber === trackingInfo.invoiceNumber);

        const newBox = {
          id: Date.now(),
          trackingNumber: numberToScan,
          orderData: {
            invoice: invoiceResult.data.invoice,
            tracking: {
              trackingNumber: numberToScan,
              invoiceNumber: trackingInfo.invoiceNumber,
              orderNumber: trackingInfo.orderNumber,
              carrierName: trackingInfo.carrierName,
              supplier: trackingInfo.supplier || null
            }
          },
          scannedAt: new Date(),
          status: 'scanned'
        };

        setScannedBoxes(prev => [...prev, newBox]);
        setLastScanned(numberToScan);
        setTrackingNumber('');
        setError('');

        if (scannerInputRef.current) {
          scannerInputRef.current.focus();
        }

        const allLines = await getExpectedLines();
        const rawLines = invoiceResult.data.invoice?.lines || [];
        const invoiceItems = rawLines.map(normalizeInvoiceLine).filter(Boolean);

        if (invoiceItems.length === 0) {
          console.warn('No invoice items found in API response');
          setExpectedLines([]);
        } else {
          const matchingLines = getMatchingLines(invoiceItems, allLines);
          setExpectedLines(matchingLines);
          console.log('Expected lines:', {
            allCount: allLines.length,
            invoiceItemsCount: invoiceItems.length,
            matchingCount: matchingLines.length,
            matching: matchingLines.map(l => ({ design: l.designName, style: l.garmentStyle, color: l.garmentColor }))
          });
        }
      } else {
        setError(`Failed to get invoice details: ${invoiceResult.error}`);
      }
    } catch (error) {
      console.error('Error scanning tracking number:', error);
      setError('Error looking up tracking number. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle key press in tracking input
  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleScanSubmit();
    }
  };

  // Clear all scanned boxes
  const handleClearAll = () => {
    setScannedBoxes([]);
    setExpectedLines([]);
    setSanmarOrderContents({});
    fetchedSanMarOrdersRef.current = new Set();
    setError('');
  };

  // Clear tracking data
  const handleClearTrackingData = () => {
    localStorage.removeItem('ssTrackingData');
    setTrackingMap({});
    setTrackingDataLoaded(false);
    setTrackingDataMetadata(null);
    setScannedBoxes([]);
    setExpectedLines([]);
    setSanmarOrderContents({});
    fetchedSanMarOrdersRef.current = new Set();
    setError('');
  };

  // Remove individual box
  const handleRemoveBox = (boxId) => {
    setScannedBoxes(prev => prev.filter(box => box.id !== boxId));
  };

  // Open order view modal
  const handleViewOrder = (orderData) => {
    setSelectedOrder(orderData);
    setOrderViewOpen(true);
  };

  // Close order view modal
  const handleCloseOrderView = () => {
    setOrderViewOpen(false);
    setSelectedOrder(null);
  };

  // Open package contents modal
  const handleViewPackage = (box) => {
    setSelectedPackage(box);
    setPackageModalOpen(true);
  };

  // Close package contents modal
  const handleClosePackageModal = () => {
    setPackageModalOpen(false);
    setSelectedPackage(null);
  };

  // Open arrival confirmation modal
  const handleMarkAsArrivedClick = (line) => {
    setSelectedLineForArrival(line);
    setArrivalModalOpen(true);
  };

  // Close arrival modal
  const handleCloseArrivalModal = () => {
    setArrivalModalOpen(false);
    setSelectedLineForArrival(null);
  };

  // Mark line as IN only (no sticker)
  const handleMarkAsIn = async () => {
    if (!selectedLineForArrival) return;
    
    setIsMarkingArrival(true);
    
    try {
      const lineRef = doc(db, 'orderLines', selectedLineForArrival.id);
      // Check if isCut is already true - if so, status should be READY_TO_PRINT.
      // Never downgrade a finished (printed/completed) line back into the print queue.
      const newStatus = lineIsFinalized(selectedLineForArrival)
        ? selectedLineForArrival.status
        : (selectedLineForArrival.isCut ? LINE_STATUSES.READY_TO_PRINT : LINE_STATUSES.IN_NOT_CUT);
      await updateDoc(lineRef, {
        status: newStatus,
        isIn: true,
        countedInAt: new Date(),
      });
      await syncOrderStatusFromLines(db, selectedLineForArrival.orderId);
      
      // Remove from expected lines
      setExpectedLines(prev => prev.filter(line => line.id !== selectedLineForArrival.id));
      setArrivalSuccessMessage('Line marked as received');
      setTimeout(() => setArrivalSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error updating line:', error);
      alert('Failed to update line');
    }
    
    setIsMarkingArrival(false);
    handleCloseArrivalModal();
  };

  // Mark line as IN and print sticker
  const handleMarkAsInAndPrint = async () => {
    if (!selectedLineForArrival) return;
    
    setIsMarkingArrival(true);
    setIsPrinting(true);
    
    try {
      const { line: freshLine, order: orderData } = await fetchStickerPrintPayload(
        db,
        selectedLineForArrival.id,
        selectedLineForArrival.orderId
      );
      const lineForStatus = freshLine || selectedLineForArrival;

      // First update the database
      const lineRef = doc(db, 'orderLines', selectedLineForArrival.id);
      // Check if isCut is already true - if so, status should be READY_TO_PRINT.
      // Never downgrade a finished (printed/completed) line back into the print queue.
      const newStatus = lineIsFinalized(lineForStatus)
        ? lineForStatus.status
        : (lineForStatus.isCut ? LINE_STATUSES.READY_TO_PRINT : LINE_STATUSES.IN_NOT_CUT);
      await updateDoc(lineRef, {
        status: newStatus,
        isIn: true,
        countedInAt: new Date(),
      });
      await syncOrderStatusFromLines(db, lineForStatus.orderId || selectedLineForArrival.orderId);
      
      // Print from fresh Firestore data (not the list cache)
      setStickerToPrint({ 
        line: freshLine, 
        order: orderData 
      });
      
      // Remove from expected lines
      setExpectedLines(prev => prev.filter(line => line.id !== selectedLineForArrival.id));
      setArrivalSuccessMessage('Line marked as received - printing sticker...');
      setTimeout(() => setArrivalSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error updating line:', error);
      alert('Failed to update line');
      setIsPrinting(false);
    }
    
    setIsMarkingArrival(false);
    handleCloseArrivalModal();
  };

  // Group invoice items by product (same title, color, brand, style). Accept raw API lines; normalize first.
  const groupInvoiceItems = (items) => {
    const grouped = {};
    const normalized = (items || []).map(normalizeInvoiceLine).filter(Boolean);

    normalized.forEach(item => {
      const key = `${item.title}|${item.colorName}|${item.brandName}|${item.styleName}`;

      if (!grouped[key]) {
        grouped[key] = {
          title: item.title,
          colorName: item.colorName,
          brandName: item.brandName,
          styleName: item.styleName,
          sizes: {},
          totalQuantity: 0
        };
      }

      grouped[key].sizes[item.sizeName] = (grouped[key].sizes[item.sizeName] || 0) + item.qtyOrdered;
      grouped[key].totalQuantity += item.qtyOrdered;
    });

    return Object.values(grouped);
  };

  // Render grouped invoice item
  const renderInvoiceItem = (groupedItem, index) => {
    const sizeEntries = Object.entries(groupedItem.sizes);
    const hasMultipleSizes = sizeEntries.length > 1;
    
    return (
      <InvoiceItem key={index}>
        <InvoiceItemHeader>
          <InvoiceItemTitle>{groupedItem.title}</InvoiceItemTitle>
          <InvoiceItemQuantity>{groupedItem.totalQuantity} pieces</InvoiceItemQuantity>
        </InvoiceItemHeader>
        <InvoiceItemDetails>
          <Chip>{groupedItem.colorName}</Chip>
          <Chip>{groupedItem.brandName} {groupedItem.styleName}</Chip>
        </InvoiceItemDetails>
        
        {hasMultipleSizes && (
          <SizeGrid>
            {sizeEntries.map(([size, qty]) => (
              <SizeChip key={size}>
                {size}: {qty}
              </SizeChip>
            ))}
          </SizeGrid>
        )}
      </InvoiceItem>
    );
  };

  // Render expected line item
  const renderExpectedLine = (line) => {
    const allRawLines = scannedBoxes.flatMap(box => box.orderData?.invoice?.lines || []);
    const allNormalized = allRawLines.map(normalizeInvoiceLine).filter(Boolean);
    const isMatched = isLineMatched(line, allNormalized);
    
    return (
      <ExpectedLineItem key={line.id} isMatched={isMatched}>
        <ExpectedLineHeader>
          <ExpectedLineTitle>
            {line.designName} - {line.customerName}
          </ExpectedLineTitle>
          <ExpectedLineStatus isMatched={isMatched}>
            {isMatched ? '✓ Matched' : '⏳ Waiting'}
          </ExpectedLineStatus>
        </ExpectedLineHeader>
        <div style={{ marginBottom: '8px' }}>
          <Text variant="subtitle2" color="rgba(255, 255, 255, 0.7)">
            {line.garmentStyle} - {line.garmentColor}
          </Text>
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {Object.entries(line.sizes).map(([size, qty]) => (
            <Chip key={size}>
              {size}: {qty}
            </Chip>
          ))}
        </div>
        <div style={{ marginTop: '8px' }}>
          <Chip color="primary">
            Total: {line.totalPieces} pieces
          </Chip>
        </div>
      </ExpectedLineItem>
    );
  };

  // Render scanned box item (simplified for the list)
  const renderScannedBox = (box) => {
    const order = box.orderData?.invoice;
    const items = order?.lines || [];

    return (
      <ListItem key={box.id}>
        <ListItemContent>
          <FlexContainer>
            <Text variant="subtitle2" color="rgba(255, 255, 255, 0.9)">
              📦 {box.trackingNumber}
            </Text>
            <Chip>
              Invoice: {order?.invoiceNumber || 'N/A'}
            </Chip>
          </FlexContainer>
          <Text variant="body2" color="rgba(255, 255, 255, 0.7)">
            {items.length} items • {items.reduce((sum, item) => sum + (item.qtyOrdered || 0), 0)} total pieces
          </Text>
        </ListItemContent>
        
        <ListItemActions>
          <IconButton onClick={() => handleViewPackage(box)}>
            <ViewIcon fontSize="small" />
          </IconButton>
          <IconButton onClick={() => handleRemoveBox(box.id)}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </ListItemActions>
      </ListItem>
    );
  };

  // Get all invoice items from all scanned boxes for the center panel
  const allInvoiceItems = scannedBoxes.flatMap(box => {
    const items = box.orderData?.invoice?.lines || [];
    return groupInvoiceItems(items);
  });

  // Total pieces across all scanned boxes
  const totalPieces = scannedBoxes.reduce((sum, box) => {
    const raw = box.orderData?.invoice?.lines || [];
    const items = raw.map(normalizeInvoiceLine).filter(Boolean);
    return sum + items.reduce((s, item) => s + (item.qtyOrdered || 0), 0);
  }, 0);

  return (
    <Container>
      {/* ============ DESKTOP LAYOUT ============ */}
      <DesktopLayout>
        {initializing ? (
          <InitializingMessage>
            <LoadingSpinner />
            Initializing API and loading tracking data...
          </InitializingMessage>
        ) : (
          <>
            {/* Desktop Header: dots + count, then centered scanner */}
            <DesktopHeader>
              <HeaderTopRow>
                <SupplierDotsRow>
                  <SupplierDotItem title="S&S Activewear">
                    <SupplierDot $connected={trackingDataLoaded} />
                    <span>S&S</span>
                  </SupplierDotItem>
                  <SupplierDotItem title="SanMar">
                    <SupplierDot $connected={trackingDataLoaded && sanmarStatus === 'connected'} />
                    <span>SanMar</span>
                  </SupplierDotItem>
                </SupplierDotsRow>
                {trackingDataLoaded && trackingDataMetadata && (
                  <TrackingSummary>
                    {Object.keys(trackingMap).length} tracking numbers found ({trackingDataMetadata.startDate} – {trackingDataMetadata.endDate})
                  </TrackingSummary>
                )}
              </HeaderTopRow>
              <ScannerInputRow>
                <ScannerIcon style={{ color: 'rgba(255, 255, 255, 0.5)' }} />
                <CompactScannerInput
                  ref={scannerInputRef}
                  type="text"
                  value={trackingNumber}
                  onChange={(e) => {
                    setTrackingNumber(e.target.value.toUpperCase());
                    if (error) setError('');
                  }}
                  onKeyPress={handleKeyPress}
                  placeholder="Scan or enter tracking number..."
                  disabled={loading || !trackingDataLoaded}
                  autoFocus
                  autoComplete="off"
                />
                {loading && <LoadingSpinner />}
                <ScanIndicator active={lastScanned}>
                  {lastScanned ? (
                    <>✓ Last: {lastScanned.slice(-8)}</>
                  ) : (
                    <>Ready to scan</>
                  )}
                </ScanIndicator>
              </ScannerInputRow>
            </DesktopHeader>

            {error && (
              <Alert severity="error" style={{ marginBottom: '16px' }}>
                {error}
              </Alert>
            )}

            {/* Main 3-Column Grid */}
            <MainGrid>
              {/* Left Panel: Scanned Boxes */}
              <SidePanel>
                <PanelHeaderStyled>
                  <PanelTitleStyled>
                    <InventoryIcon fontSize="small" />
                    Scanned Boxes
                    {scannedBoxes.length > 0 && (
                      <PanelBadge>{scannedBoxes.length}</PanelBadge>
                    )}
                  </PanelTitleStyled>
                  {scannedBoxes.length > 0 && (
                    <SmallIconButton onClick={handleClearAll} title="Clear all">
                      <ClearIcon fontSize="small" />
                    </SmallIconButton>
                  )}
                </PanelHeaderStyled>
                <PanelContent>
                  {scannedBoxes.length === 0 ? (
                    <EmptyStateStyled>
                      <InventoryIcon />
                      <div>No boxes scanned yet</div>
                      <div style={{ fontSize: '0.8rem', marginTop: '8px' }}>
                        Scan a tracking number to begin
                      </div>
                    </EmptyStateStyled>
                  ) : (
                    scannedBoxes.map((box, index) => {
                      const raw = box.orderData?.invoice?.lines || [];
                      const items = raw.map(normalizeInvoiceLine).filter(Boolean);
                      const pieces = items.reduce((sum, item) => sum + (item.qtyOrdered || 0), 0);
                      const isLatest = index === scannedBoxes.length - 1;
                      
                      return (
                        <ScannedBoxCard 
                          key={box.id} 
                          isLatest={isLatest}
                          onClick={() => handleViewPackage(box)}
                        >
                          <BoxCardHeader>
                            <div>
                              <BoxTrackingNumber>
                                📦 {box.trackingNumber}
                              </BoxTrackingNumber>
                              <BoxInvoice>
                                {box.orderData?.tracking?.supplier === 'sanmar'
                                  ? (() => {
                                      const o = box.orderData?.tracking?.orderNumber;
                                      const so = box.orderData?.tracking?.salesOrderNumber;
                                      const num = (o && String(o) !== 'Not Available') ? o : so;
                                      return num ? `Order #${num}` : 'Order #—';
                                    })()
                                  : `Invoice: ${box.orderData?.invoice?.invoiceNumber || 'N/A'}`}
                              </BoxInvoice>
                            </div>
                            <BoxActions className="box-actions">
                              <SmallIconButton 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleViewPackage(box);
                                }}
                                title="View contents"
                              >
                                <ViewIcon fontSize="small" />
                              </SmallIconButton>
                              <SmallIconButton 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRemoveBox(box.id);
                                }}
                                title="Remove"
                              >
                                <DeleteIcon fontSize="small" />
                              </SmallIconButton>
                            </BoxActions>
                          </BoxCardHeader>
                          <BoxStats>
                            {box.orderData?.tracking?.supplier === 'sanmar' && !box.orderData?.invoice?.lines?.length
                              ? (
                                  <>
                                    <span>Order #{(box.orderData?.tracking?.orderNumber && String(box.orderData.tracking.orderNumber) !== 'Not Available') ? box.orderData.tracking.orderNumber : (box.orderData?.tracking?.salesOrderNumber || '—')}</span>
                                    <span>•</span>
                                    <span>Contents not available from API</span>
                                  </>
                                )
                              : (
                                  <>
                                    <span>{items.length} items</span>
                                    <span>•</span>
                                    <span>{pieces} pieces</span>
                                  </>
                                )}
                          </BoxStats>
                        </ScannedBoxCard>
                      );
                    })
                  )}
                  {speeDeeShipmentStats.some(s => s.missing.length > 0) && (
                    <SpeeDeeAlertBox>
                      <SpeeDeeAlertTitle>SpeeDee shipment check</SpeeDeeAlertTitle>
                      {speeDeeShipmentStats.filter(s => s.missing.length > 0).map((ship) => (
                        <SpeeDeeAlertItem key={ship.canonical}>
                          Invoice {ship.invoiceNumber}: Expected {ship.expectedTotal} box{ship.expectedTotal !== 1 ? 'es' : ''}; not yet scanned: {ship.missing.map(n => String(n).padStart(3, '0')).join(', ')}
                        </SpeeDeeAlertItem>
                      ))}
                    </SpeeDeeAlertBox>
                  )}
                  {sanmarOrderStats.length > 0 && (
                    <SpeeDeeAlertBox style={{ marginTop: 12, borderColor: 'rgba(33, 150, 243, 0.5)', background: 'rgba(33, 150, 243, 0.1)' }}>
                      <SpeeDeeAlertTitle>SanMar order (scan all boxes to load contents)</SpeeDeeAlertTitle>
                      {sanmarOrderStats.map((stat) => (
                        <SpeeDeeAlertItem key={stat.orderNumber}>
                          Order SO-{stat.orderNumber}: {stat.allScanned
                            ? `All ${stat.totalCount} box${stat.totalCount !== 1 ? 'es' : ''} scanned. ${sanmarOrderContents[stat.orderNumber]?.invoice?.lines?.length ? 'Contents loaded.' : 'Loading contents…'}`
                            : `${stat.scannedCount} of ${stat.totalCount} box${stat.totalCount !== 1 ? 'es' : ''} scanned. Remaining: ${stat.missingTrackingNumbers.map(t => t.slice(-6)).join(', ')}`}
                        </SpeeDeeAlertItem>
                      ))}
                    </SpeeDeeAlertBox>
                  )}
                </PanelContent>
              </SidePanel>

              {/* Center Panel: Package Contents */}
              <CenterPanel>
                <PanelHeaderStyled>
                  <PanelTitleStyled>
                    <AssignmentIcon fontSize="small" />
                    Package Contents
                    {totalPieces > 0 && (
                      <PanelBadge>{totalPieces} pieces</PanelBadge>
                    )}
                  </PanelTitleStyled>
                </PanelHeaderStyled>
                <PanelContent>
                  {allInvoiceItems.length === 0 ? (
                    <EmptyStateStyled>
                      <AssignmentIcon />
                      <div>No items to display</div>
                      <div style={{ fontSize: '0.8rem', marginTop: '8px' }}>
                        Scan a box to see its contents
                      </div>
                    </EmptyStateStyled>
                  ) : (
                    <ContentGrid>
                      {allInvoiceItems.map((item, index) => (
                        <ProductRow key={index}>
                          <ProductInfo>
                            <span className="brand">{item.brandName}</span>
                            <span className="separator">-</span>
                            <span>{item.styleName}</span>
                            <span className="separator">-</span>
                            <span className="color">{item.colorName}</span>
                          </ProductInfo>
                          <SizeGridRow>
                            {Object.entries(item.sizes).map(([size, qty]) => (
                              <SizeCell key={size}>
                                <span className="size-label">{size}</span>
                                <span className="size-qty">{qty}</span>
                              </SizeCell>
                            ))}
                          </SizeGridRow>
                          <TotalBadge>{item.totalQuantity}</TotalBadge>
                        </ProductRow>
                      ))}
                    </ContentGrid>
                  )}
                </PanelContent>
              </CenterPanel>

              {/* Right Panel: Expected Lines */}
              <SidePanel>
                <PanelHeaderStyled>
                  <PanelTitleStyled>
                    <AssignmentIcon fontSize="small" />
                    Expected Lines
                    {expectedLines.length > 0 && (
                      <PanelBadge color="rgba(255, 193, 7, 0.8)">{expectedLines.length}</PanelBadge>
                    )}
                  </PanelTitleStyled>
                </PanelHeaderStyled>
                <PanelContent>
                  {expectedLines.length === 0 ? (
                    <EmptyStateStyled>
                      <AssignmentIcon />
                      <div>No expected lines</div>
                      <div style={{ fontSize: '0.8rem', marginTop: '8px' }}>
                        Lines waiting on garments will appear here
                      </div>
                    </EmptyStateStyled>
                  ) : (
                    expectedLines.map(line => {
                      const receivedQtys = getReceivedQuantities(line, allInvoiceItems);
                      const hasAnyReceived = Object.keys(receivedQtys).length > 0;
                      
                      // Calculate totals
                      let totalExpected = 0;
                      let totalReceived = 0;
                      Object.entries(line.sizes).forEach(([size, qty]) => {
                        totalExpected += qty;
                        totalReceived += Math.min(receivedQtys[size] || 0, qty);
                      });
                      
                      const fullyReceived = totalReceived >= totalExpected;
                      const partiallyReceived = totalReceived > 0 && totalReceived < totalExpected;
                      
                      return (
                        <ExpectedCard key={line.id} matched={fullyReceived}>
                          <ExpectedHeader>
                            <ExpectedTitle>{line.designName}</ExpectedTitle>
                            <ExpectedStatus matched={fullyReceived}>
                              {fullyReceived ? '✓ Complete' : partiallyReceived ? '◐ Partial' : '⏳ Waiting'}
                            </ExpectedStatus>
                          </ExpectedHeader>
                          <ExpectedMeta>
                            {line.customerName} • {line.garmentStyle} - {line.garmentColor}
                          </ExpectedMeta>
                          <ExpectedSizeGrid>
                            {Object.entries(line.sizes).map(([size, expectedQty]) => {
                              const receivedQty = receivedQtys[size] || 0;
                              const isFully = receivedQty >= expectedQty;
                              const isPartial = receivedQty > 0 && receivedQty < expectedQty;
                              
                              return (
                                <ExpectedSizeCell 
                                  key={size} 
                                  fullyReceived={isFully}
                                  partiallyReceived={isPartial}
                                >
                                  <ExpectedSizeLabel>{size}</ExpectedSizeLabel>
                                  <ExpectedSizeQty 
                                    fullyReceived={isFully}
                                    partiallyReceived={isPartial}
                                  >
                                    {expectedQty}
                                  </ExpectedSizeQty>
                                  {receivedQty > 0 && (
                                    <ReceivedIndicator>
                                      {isFully ? '✓' : `${receivedQty}/${expectedQty}`}
                                    </ReceivedIndicator>
                                  )}
                                </ExpectedSizeCell>
                              );
                            })}
                          </ExpectedSizeGrid>
                          <ExpectedTotalRow>
                            <div>
                              <ReceivedCount>{totalReceived}</ReceivedCount>
                              <span style={{ color: 'rgba(255,255,255,0.3)' }}> / </span>
                              <PendingCount>{totalExpected} pieces</PendingCount>
                            </div>
                            {totalExpected - totalReceived > 0 && (
                              <PendingCount>
                                {totalExpected - totalReceived} remaining
                              </PendingCount>
                            )}
                          </ExpectedTotalRow>
                          
                          {/* Show Mark as Arrived button when fully received */}
                          {fullyReceived && (
                            <MarkArrivedButton onClick={(e) => {
                              e.stopPropagation();
                              handleMarkAsArrivedClick(line);
                            }}>
                              <CheckCircleIcon />
                              Mark as Arrived
                            </MarkArrivedButton>
                          )}
                        </ExpectedCard>
                      );
                    })
                  )}
                </PanelContent>
              </SidePanel>
            </MainGrid>
          </>
        )}
      </DesktopLayout>

      {/* ============ MOBILE LAYOUT (Original) ============ */}
      <MobileLayout>
        {/* API Status and Scanner Section */}
        <StyledSection>
          {initializing ? (
            <InitializingMessage>
              <LoadingSpinner />
              Initializing API and loading tracking data...
            </InitializingMessage>
          ) : (
            <>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
                <SupplierDotsRow>
                  <SupplierDotItem>
                    <SupplierDot $connected={trackingDataLoaded} />
                    <span>S&S</span>
                  </SupplierDotItem>
                  <SupplierDotItem>
                    <SupplierDot $connected={trackingDataLoaded && sanmarStatus === 'connected'} />
                    <span>SanMar</span>
                  </SupplierDotItem>
                </SupplierDotsRow>
                {trackingDataLoaded && trackingDataMetadata && (
                  <TrackingSummary>
                    {Object.keys(trackingMap).length} tracking numbers found ({trackingDataMetadata.startDate} – {trackingDataMetadata.endDate})
                  </TrackingSummary>
                )}
              </div>
              <div style={{ display: 'flex', justifyContent: 'center', marginTop: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', maxWidth: '400px' }}>
                  <ScannerIcon style={{ color: 'rgba(255, 255, 255, 0.5)', flexShrink: 0 }} />
                  <CompactScannerInput
                    ref={scannerInputRef}
                    type="text"
                    value={trackingNumber}
                    onChange={(e) => {
                      setTrackingNumber(e.target.value.toUpperCase());
                      if (error) setError('');
                    }}
                    onKeyPress={handleKeyPress}
                    placeholder="Scan or enter tracking number..."
                    disabled={loading || !trackingDataLoaded}
                    autoComplete="off"
                    style={{ minWidth: 0 }}
                  />
                </div>
              </div>
              {/* Mobile: Camera Scan Button */}
              <Button
                onClick={startCamera}
                disabled={loading || !trackingDataLoaded}
                style={{ width: '100%', marginTop: '1rem' }}
              >
                <CameraIcon fontSize="small" style={{ marginRight: '8px' }} />
                Camera Scan
              </Button>

              {error && (
                <Alert severity="error" style={{ marginTop: '1rem' }}>
                  {error}
                </Alert>
              )}
            </>
          )}
        </StyledSection>

        {/* Expected Stickers - Always show */}
        <StyledSection>
          <PanelHeader>
            <AssignmentIcon fontSize="small" />
            <PanelTitle>Expected Stickers</PanelTitle>
          </PanelHeader>
          
          {expectedLines.length === 0 ? (
            <EmptyState>
              No expected lines found. Lines with "waiting on garments" status will appear here.
            </EmptyState>
          ) : (
            expectedLines.map(renderExpectedLine)
          )}
        </StyledSection>

        {/* Package Contents - Show when boxes are scanned */}
        {scannedBoxes.length > 0 && (
          <StyledSection>
            <PanelHeader>
              <InventoryIcon fontSize="small" />
              <PanelTitle>Package Contents</PanelTitle>
            </PanelHeader>
            
            {scannedBoxes.map(box => {
              const items = box.orderData?.invoice?.lines || [];
              const groupedItems = groupInvoiceItems(items);
              return (
                <div key={box.id} style={{ marginBottom: '24px' }}>
                  <Text variant="subtitle2" color="rgba(255, 255, 255, 0.9)" mb="12px">
                    📦 {box.trackingNumber}
                  </Text>
                  {groupedItems.length === 0 ? (
                    <EmptyState>
                      No items found in this invoice
                    </EmptyState>
                  ) : (
                    groupedItems.map(renderInvoiceItem)
                  )}
                </div>
              );
            })}
          </StyledSection>
        )}

        {/* Scanned Boxes List */}
        {scannedBoxes.length > 0 && (
          <StyledSection>
            <FlexBetween>
              <SectionTitle>Scanned Boxes ({scannedBoxes.length})</SectionTitle>
              <Button variant="outlined" onClick={handleClearAll}>
                <ClearIcon fontSize="small" />
                Clear All
              </Button>
            </FlexBetween>

            <List>
              {scannedBoxes.map(renderScannedBox)}
            </List>
          </StyledSection>
        )}
        {scannedBoxes.length > 0 && speeDeeShipmentStats.some(s => s.missing.length > 0) && (
          <StyledSection>
            <SpeeDeeAlertBox>
              <SpeeDeeAlertTitle>SpeeDee shipment check</SpeeDeeAlertTitle>
              {speeDeeShipmentStats.filter(s => s.missing.length > 0).map((ship) => (
                <SpeeDeeAlertItem key={ship.canonical}>
                  Invoice {ship.invoiceNumber}: Expected {ship.expectedTotal} box{ship.expectedTotal !== 1 ? 'es' : ''}; not yet scanned: {ship.missing.map(n => String(n).padStart(3, '0')).join(', ')}
                </SpeeDeeAlertItem>
              ))}
            </SpeeDeeAlertBox>
          </StyledSection>
        )}
        {scannedBoxes.length > 0 && sanmarOrderStats.length > 0 && (
          <StyledSection>
            <SpeeDeeAlertBox style={{ borderColor: 'rgba(33, 150, 243, 0.5)', background: 'rgba(33, 150, 243, 0.1)' }}>
              <SpeeDeeAlertTitle>SanMar order (scan all boxes to load contents)</SpeeDeeAlertTitle>
              {sanmarOrderStats.map((stat) => (
                <SpeeDeeAlertItem key={stat.orderNumber}>
                  Order SO-{stat.orderNumber}: {stat.allScanned
                    ? `All ${stat.totalCount} box${stat.totalCount !== 1 ? 'es' : ''} scanned. ${sanmarOrderContents[stat.orderNumber]?.invoice?.lines?.length ? 'Contents loaded.' : 'Loading contents…'}`
                    : `${stat.scannedCount} of ${stat.totalCount} box${stat.totalCount !== 1 ? 'es' : ''} scanned. Remaining: ${stat.missingTrackingNumbers.map(t => t.slice(-6)).join(', ')}`}
                </SpeeDeeAlertItem>
              ))}
            </SpeeDeeAlertBox>
          </StyledSection>
        )}
      </MobileLayout>

      {/* ============ SHARED MODALS ============ */}
      
      {/* Order View Modal */}
      {orderViewOpen && (
        <Modal onClick={handleCloseOrderView}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Order Details</ModalTitle>
              <CloseButton onClick={handleCloseOrderView}>
                ✕
              </CloseButton>
            </ModalHeader>
            {selectedOrder && (
              <OrderView 
                order={selectedOrder.invoice} 
                onClose={handleCloseOrderView}
              />
            )}
          </ModalContent>
        </Modal>
      )}

      {/* Package Contents Modal */}
      {packageModalOpen && selectedPackage && (
        <Modal onClick={handleClosePackageModal}>
          <PackageModalContent onClick={(e) => e.stopPropagation()}>
            <PackageModalHeader>
              <PackageHeaderInfo>
                <PackageTitle>
                  <InventoryIcon />
                  Package Contents
                </PackageTitle>
                <PackageSubtitle>
                  <span>📦 {selectedPackage.trackingNumber}</span>
                  <span>•</span>
                  <span>
                    {selectedPackage.orderData?.tracking?.supplier === 'sanmar'
                      ? (() => {
                          const o = selectedPackage.orderData?.tracking?.orderNumber;
                          const so = selectedPackage.orderData?.tracking?.salesOrderNumber;
                          const num = (o && String(o) !== 'Not Available') ? o : so;
                          return num ? `Order #${num}` : 'Order #—';
                        })()
                      : `Invoice: ${selectedPackage.orderData?.invoice?.invoiceNumber || 'N/A'}`}
                  </span>
                </PackageSubtitle>
              </PackageHeaderInfo>
              <PackageModalCloseButton onClick={handleClosePackageModal}>
                ✕
              </PackageModalCloseButton>
            </PackageModalHeader>
            
            <PackageModalBody>
              {(() => {
                const items = selectedPackage.orderData?.invoice?.lines || [];
                const isSanMarNoContents = selectedPackage.orderData?.tracking?.supplier === 'sanmar' && !items?.length;
                const groupedItems = groupInvoiceItems(items);
                const totalPcs = items.reduce((sum, item) => sum + (item.qtyOrdered || 0), 0);
                const uniqueStyles = new Set(groupedItems.map(i => `${i.brandName} ${i.styleName}`)).size;

                if (isSanMarNoContents) {
                  return (
                    <div style={{ padding: '24px', color: 'rgba(255,255,255,0.8)', textAlign: 'center' }}>
                      <p>Package contents are not available from SanMar for this order.</p>
                      <p style={{ marginTop: '12px', fontSize: '0.9rem' }}>
                        SanMar ties one order (SO-…) to multiple tracking numbers for the whole shipment, so we can&apos;t show contents for this box only.
                      </p>
                      <p style={{ marginTop: '12px', fontSize: '0.9rem' }}>You can still mark expected lines as arrived from the list when you&apos;ve confirmed the delivery.</p>
                    </div>
                  );
                }

                const isSanMarWithContents = selectedPackage.orderData?.tracking?.supplier === 'sanmar' && items?.length > 0;
                return (
                  <>
                    {isSanMarWithContents && (
                      <p style={{ padding: '8px 12px', marginBottom: '12px', background: 'rgba(255,193,7,0.15)', borderRadius: '6px', fontSize: '0.85rem', color: 'rgba(255,255,255,0.9)' }}>
                        SanMar ties one order to multiple tracking numbers. Shown below are contents for the <strong>full shipment (all boxes)</strong>, not this box only.
                      </p>
                    )}
                    <PackageSummaryBar>
                      <SummaryCard>
                        <SummaryValue>{totalPcs}</SummaryValue>
                        <SummaryLabel>Total Pieces</SummaryLabel>
                      </SummaryCard>
                      <SummaryCard>
                        <SummaryValue color="white">{groupedItems.length}</SummaryValue>
                        <SummaryLabel>Line Items</SummaryLabel>
                      </SummaryCard>
                      <SummaryCard>
                        <SummaryValue color="#64b5f6">{uniqueStyles}</SummaryValue>
                        <SummaryLabel>Unique Styles</SummaryLabel>
                      </SummaryCard>
                    </PackageSummaryBar>
                    
                    <PackageItemsList>
                      {groupedItems.map((item, index) => (
                        <PackageItemRow key={index}>
                          <PackageItemInfo>
                            <PackageItemTitle>
                              {item.brandName} - {item.styleName}
                            </PackageItemTitle>
                            <PackageItemMeta>
                              {item.colorName}
                            </PackageItemMeta>
                          </PackageItemInfo>
                          <PackageItemSizes>
                            {Object.entries(item.sizes).map(([size, qty]) => (
                              <PackageSizeChip key={size}>
                                <span className="size-label">{size}</span>
                                <span className="size-qty">{qty}</span>
                              </PackageSizeChip>
                            ))}
                          </PackageItemSizes>
                          <PackageItemTotal>
                            {item.totalQuantity}
                          </PackageItemTotal>
                        </PackageItemRow>
                      ))}
                    </PackageItemsList>
                  </>
                );
              })()}
            </PackageModalBody>
            
            <PackageModalFooter>
              <PackageFooterInfo>
                Scanned at {selectedPackage.scannedAt ? new Date(selectedPackage.scannedAt).toLocaleTimeString() : 'N/A'}
              </PackageFooterInfo>
              <PackageFooterActions>
                <PackageButton 
                  onClick={() => {
                    handleRemoveBox(selectedPackage.id);
                    handleClosePackageModal();
                  }}
                >
                  <DeleteIcon fontSize="small" />
                  Remove Box
                </PackageButton>
                <PackageButton variant="primary" onClick={handleClosePackageModal}>
                  Done
                </PackageButton>
              </PackageFooterActions>
            </PackageModalFooter>
          </PackageModalContent>
        </Modal>
      )}

      {/* Arrival Confirmation Dialog */}
      <ArrivalConfirmationModal
        open={arrivalModalOpen}
        onClose={handleCloseArrivalModal}
        line={selectedLineForArrival}
        onMarkAsIn={handleMarkAsIn}
        onMarkAsInAndPrint={handleMarkAsInAndPrint}
        isUpdating={isMarkingArrival}
        isPrinting={isPrinting}
      />

      {/* Hidden JobSticker for printing */}
      {stickerToPrint && (
        <div style={{ display: 'none' }}>
          <JobSticker
            line={stickerToPrint.line}
            order={stickerToPrint.order}
            onPdfGenerated={async (pdfData) => {
              if (!pdfData) {
                setIsPrinting(false);
                setStickerToPrint(null);
                return;
              }
              try {
                const result = await printLabelBase64(pdfData, { type: 'line', tray: 1 });
                console.log('Print job submitted successfully:', result.jobId);
                setArrivalSuccessMessage('Sticker printed successfully!');
                setTimeout(() => setArrivalSuccessMessage(''), 3000);
              } catch (error) {
                console.error('Print error details:', error);
                alert(`Failed to print: ${error.message}`);
              } finally {
                setIsPrinting(false);
                setStickerToPrint(null);
              }
            }}
          />
        </div>
      )}

      {/* Success Message Toast */}
      {arrivalSuccessMessage && (
        <div style={{
          position: 'fixed',
          bottom: '24px',
          right: '24px',
          background: '#4caf50',
          color: 'white',
          padding: '12px 20px',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          fontWeight: 500
        }}>
          <CheckCircleIcon />
          {arrivalSuccessMessage}
        </div>
      )}

      {/* Camera Modal */}
      {cameraOpen && (
        <CameraModal>
          <CameraHeader>
            <div>Scan Barcode</div>
            <CloseButton onClick={stopCamera}>
              <CloseIcon />
            </CloseButton>
          </CameraHeader>
          <CameraContainer>
            <Video 
              ref={videoRef} 
              autoPlay 
              playsInline 
              muted
              onLoadedMetadata={() => console.log('🟢 Video metadata loaded')}
              onCanPlay={() => console.log('🟢 Video can play')}
              onError={(e) => console.error('🔴 Video error:', e)}
            />
            <CameraOverlay />
            <CameraInstructions>
              Point camera at barcode or QR code
            </CameraInstructions>
            {cameraError && (
              <div style={{ 
                color: 'red', 
                padding: '10px', 
                textAlign: 'center',
                backgroundColor: 'rgba(255,0,0,0.1)',
                margin: '10px'
              }}>
                {cameraError}
              </div>
            )}
          </CameraContainer>
        </CameraModal>
      )}
    </Container>
  );
};

export default APIReceiving;
